<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  playerId: {
    type: [String, Number],
    required: true
  },
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])

const player = ref(null)
const loading = ref(false)
const error = ref(null)

async function fetchPlayerProfile() {
  if (!props.playerId) return
  
  loading.value = true
  error.value = null
  
  try {
    // Try v2 endpoint first, fallback to v1
    let response
    try {
      response = await axios.get(`/api/v2/player/${props.playerId}`)
    } catch (e) {
      response = await axios.get(`/api/player/${props.playerId}`)
    }
    
    if (response.data) {
      // Process the API response to extract team information from roster
      let extractedTeam = response.data.team
      let extractedTeams = []
      
      // Extract team information from roster if available
      if (response.data.roster && response.data.roster.length > 0) {
        extractedTeams = response.data.roster.map(rosterItem => rosterItem.team?.team_name).filter(Boolean)
        if (extractedTeams.length > 0) {
          extractedTeam = extractedTeams[0] // Use first team as primary team
        }
      }
      
      player.value = {
        ...response.data,
        team: extractedTeam || 'Team information not available',
        position: response.data.position || 'Position not specified',
        nationality: response.data.nationality || 'Nationality not available',
        birthdate: response.data.birthdate || response.data.bd || 'Birth date not available',
        height: response.data.height_cm ? `${response.data.height_cm} cm` : 'Height not available',
        age: response.data.age || 'Age not available',
        extractedTeams: extractedTeams
      }
    } else {
      player.value = response.data
    }
  } catch (err) {
    console.error('Error fetching player profile:', err)
    error.value = 'Failed to load player profile'
  } finally {
    loading.value = false
  }
}

function closeModal() {
  emit('close')
}

// Fetch player data when modal opens
watch(() => props.show, (newValue) => {
  if (newValue && props.playerId) {
    fetchPlayerProfile()
  }
})

onMounted(() => {
  if (props.show && props.playerId) {
    fetchPlayerProfile()
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <!-- Overlay -->
      <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="closeModal">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      
      <!-- Modal Content -->
      <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <h3 class="text-xl font-semibold text-gray-900">Player Profile</h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-500 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
            @click="closeModal"
          >
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Content -->
        <div class="p-6">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <!-- Error State -->
          <div v-else-if="error" class="text-center py-12">
            <p class="text-red-500">{{ error }}</p>
          </div>
          
          <!-- Player Profile Content -->
          <div v-else-if="player" class="space-y-6">
            <!-- Player Header -->
            <div class="flex flex-col items-center pt-4 pb-6">
              <div class="w-32 h-32 rounded-full border-4 border-gray-300 shadow-md overflow-hidden bg-white mb-4">
                <img 
                  v-if="player.avatar" 
                  :src="player.avatar" 
                  alt="Player photo" 
                  class="w-full h-full object-cover"
                />
                <div v-else class="w-full h-full flex items-center justify-center bg-gray-200">
                  <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              <div class="text-center">
                <div class="text-3xl font-bold text-gray-900 mb-1">{{ player.player_name || 'Player Name' }}</div>
                <div class="text-gray-600 text-lg font-semibold uppercase tracking-wide">{{ player.team || 'Team Name' }}</div>
                <div v-if="player.player_link" class="mt-2">
                  <a 
                    :href="player.player_link" 
                    target="_blank" 
                    class="text-blue-500 hover:text-blue-700 text-sm inline-flex items-center gap-1"
                    title="View on ProBallers"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View on ProBallers
                  </a>
                </div>
              </div>
            </div>
            
            <!-- Info Grid -->
            <div class="bg-gray-50 rounded-lg px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div class="text-center">
                <div class="text-xs text-gray-500 font-medium mb-1">DATE OF BIRTH</div>
                <div class="text-gray-900 font-semibold">{{ player.birthdate }}</div>
              </div>
              <div class="text-center">
                <div class="text-xs text-gray-500 font-medium mb-1">HEIGHT</div>
                <div class="text-gray-900 font-semibold">{{ player.height }}</div>
              </div>
              <div v-if="player.nationality" class="text-center">
                <div class="text-xs text-gray-500 font-medium mb-1">NATIONALITY</div>
                <div class="text-gray-900 font-semibold">{{ player.nationality }}</div>
              </div>
              <div v-if="player.age" class="text-center">
                <div class="text-xs text-gray-500 font-medium mb-1">AGE</div>
                <div class="text-gray-900 font-semibold">{{ player.age }}</div>
              </div>
            </div>
            
            <!-- Stats Row -->
            <div class="bg-white rounded-lg flex justify-between px-6 py-4 text-center font-semibold text-lg border border-gray-200">
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-500 mb-1">PTS</div>
                <div class="text-blue-600">{{ player.pts ?? 0 }}</div>
              </div>
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-500 mb-1">REB</div>
                <div class="text-blue-600">{{ player.reb ?? 0 }}</div>
              </div>
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-500 mb-1">AST</div>
                <div class="text-blue-600">{{ player.ast ?? 0 }}</div>
              </div>
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-500 mb-1">STL</div>
                <div class="text-blue-600">{{ player.stl ?? 0 }}</div>
              </div>
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-500 mb-1">BLK</div>
                <div class="text-blue-600">{{ player.blk ?? 0 }}</div>
              </div>
            </div>
            
            <!-- Additional Info -->
            <div v-if="player.position" class="bg-gray-50 rounded-lg px-6 py-4">
              <div class="text-sm text-gray-600">
                <span class="font-medium">Position:</span> {{ player.position }}
              </div>
            </div>
            
            <!-- Player ID -->
            <div class="text-center text-sm text-gray-500">
              Player ID: {{ playerId }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>




